import { EspdResponseDataType } from "../../../../../autogen/bff-api";
import type {
  EspdResponseDto,
  EspdRequirementResponseDto,
  ResolvedEspdCriterionDto,
  ResolvedEspdRequirementDto,
  ResolvedEspdRequirementGroupDto,
} from "../../../../../autogen/bff-api";

const ON_TRUE = "GROUP_FULFILLED.ON_TRUE";
const ON_FALSE = "GROUP_FULFILLED.ON_FALSE";

const answerFor = (response: EspdResponseDto | undefined, id: string): EspdRequirementResponseDto | undefined =>
  response?.responses?.find((r) => r.requirementId === id);

const hasValue = (a: EspdRequirementResponseDto | undefined, type: ResolvedEspdRequirementDto["responseDataType"]): boolean => {
  if (!a) return false;
  switch (type) {
    case EspdResponseDataType.Indicator: return a.indicator !== null && a.indicator !== undefined;
    case EspdResponseDataType.Description: return !!a.description && a.description.trim().length > 0;
    case EspdResponseDataType.Date: return !!a.date;
    case EspdResponseDataType.Amount: return a.amount !== null && a.amount !== undefined;
    case EspdResponseDataType.Code:
    case EspdResponseDataType.CodeCountry: return !!a.code;
    case EspdResponseDataType.Percentage: return a.percentage !== null && a.percentage !== undefined;
    case EspdResponseDataType.Period: return !!a.period;
    case EspdResponseDataType.Quantity:
    case EspdResponseDataType.QuantityInteger:
    case EspdResponseDataType.QuantityYear: return a.quantity !== null && a.quantity !== undefined;
    case EspdResponseDataType.EvidenceUrl: return !!a.evidenceUrl;
    default: return false;
  }
};

const groupComplete = (group: ResolvedEspdRequirementGroupDto, response: EspdResponseDto | undefined): boolean => {
  const requirementsAnswered = group.requirements.every(
    (req) => req.optional || hasValue(answerFor(response, req.id), req.responseDataType),
  );
  if (!requirementsAnswered) return false;

  const lead = group.requirements.find((r) => r.responseDataType === EspdResponseDataType.Indicator);
  const leadAnswer = lead ? answerFor(response, lead.id)?.indicator : undefined;

  return group.subGroups.every((sub) => {
    const active = sub.processingInstruction === ON_TRUE ? leadAnswer === true
      : sub.processingInstruction === ON_FALSE ? leadAnswer === false
      : true;
    return !active || groupComplete(sub, response);
  });
};

export const isCriterionComplete = (criterion: ResolvedEspdCriterionDto, response: EspdResponseDto | undefined): boolean => {
  const gating = criterion.requirementGroups[0];
  return gating ? groupComplete(gating, response) : true;
};

export const isCriteriaComplete = (criteria: ResolvedEspdCriterionDto[], response: EspdResponseDto | undefined): boolean =>
  criteria.every((c) => isCriterionComplete(c, response));

/** Whether a single requirement's stored answer carries a value of its expected type. */
export const answerHasValue = (
  answer: EspdRequirementResponseDto | undefined,
  type: ResolvedEspdRequirementDto["responseDataType"],
): boolean => hasValue(answer, type);
